import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  IconButton,
  Grid,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  useTheme,
  alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NotificationSnackbar from "./NotificationBar";

function AddNewRBMException({ open, onClose, onSuccess }) {
  const theme = useTheme();
  const initialFormState = {
    employeeId: "",
    action: "",
    fiscalYearId: "",
    quarterId: "",
    startDate: "",
    gracePeriodDays: "",
  };

  const [formData, setFormData] = useState(initialFormState);
  const [loading, setLoading] = useState(false);
  const [fiscalData, setFiscalData] = useState({});
  const [fetchingFiscal, setFetchingFiscal] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const todayDateString = new Date().toISOString().split("T")[0];
  const isFormEmpty =
    !formData.employeeId ||
    !formData.action ||
    !formData.fiscalYearId ||
    !formData.quarterId ||
    !formData.startDate ||
    !formData.gracePeriodDays;

  useEffect(() => {
    if (open) {
      setFormData(initialFormState);
      setSnackbarOpen(false);
      fetchFiscalYearsAndQuarters();
    }
  }, [open]);

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const showSnackbar = (message, severity = "error") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const fetchFiscalYearsAndQuarters = async () => {
    try {
      setFetchingFiscal(true);
      const response = await fetch("/api/support/fiscalYears-quarters");
      if (!response.ok) {
        throw new Error("Failed to fetch fiscal years and quarters.");
      }
      const data = await response.json();
      setFiscalData(data);
    } catch (err) {
      console.error("Error fetching fiscal data:", err);
      showSnackbar(
        "Could not load fiscal years. Please try closing and reopening.",
        "error",
      );
    } finally {
      setFetchingFiscal(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "fiscalYearId") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        quarterId: "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSnackbarOpen(false);
    if (
      !formData.employeeId ||
      !formData.action ||
      !formData.fiscalYearId ||
      !formData.quarterId ||
      !formData.startDate ||
      !formData.gracePeriodDays
    ) {
      showSnackbar("Please fill in all required fields.", "warning");
      return;
    }

    try {
      setLoading(true);

      const response = await fetch("/api/support/rbm-employees-exceptions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          employeeId: formData.employeeId,
          fiscalYearId: formData.fiscalYearId,
          quarterId: formData.quarterId,
          action: formData.action,
          startDate: formData.startDate,
          gracePeriodDays: parseInt(formData.gracePeriodDays, 10),
        }),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.message || "Failed to create new exception.");
      }

      showSnackbar("Exception granted successfully!", "success");
      setTimeout(() => {
        if (onSuccess) onSuccess();
      }, 1200);
    } catch (err) {
      console.error("Error creating exception:", err);
      showSnackbar(err.message || "An unexpected error occurred.", "error");
    } finally {
      setLoading(false);
    }
  };

  const availableYears = Object.keys(fiscalData);
  const availableQuarters = formData.fiscalYearId
    ? fiscalData[formData.fiscalYearId]
    : [];

  return (
    <>
      <Dialog
        open={open}
        onClose={!loading ? onClose : undefined}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "16px",
          },
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(0, 0, 0, 0.7)",
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            p: 3,
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: "white",
              fontWeight: 600,
            }}
          >
            Add New RBM Exception
          </Typography>
          <IconButton
            onClick={onClose}
            disabled={loading}
            sx={{
              color: "white",
              bgcolor: "rgba(255, 255, 255, 0.1)",
              "&:hover": {
                bgcolor: "rgba(255, 255, 255, 0.2)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* CONTENT */}
        <DialogContent sx={{ p: 4, bgcolor: "#fcfcfc" }}>
          <Typography
            variant="body2"
            sx={{ mb: 3, color: "text.secondary", fontWeight: 500 }}
          >
            Fill out the details below to grant an RBM exception to an employee.
          </Typography>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Employee ID */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Employee ID"
                  name="employeeId"
                  value={formData.employeeId}
                  onChange={handleChange}
                  placeholder="Enter Employee ID"
                  required
                  InputProps={{
                    sx: { borderRadius: 2 },
                  }}
                />
              </Grid>

              {/* Granted Action */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Granted Action</InputLabel>
                  <Select
                    name="action"
                    value={formData.action}
                    label="Granted Action"
                    onChange={handleChange}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="canReport">canReport</MenuItem>
                    <MenuItem value="canEvaluate">canEvaluate</MenuItem>
                    <MenuItem value="canAssign">canAssign</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Fiscal Year */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required disabled={fetchingFiscal}>
                  <InputLabel>Fiscal Year</InputLabel>
                  <Select
                    name="fiscalYearId" // Mapped to fiscalYearId
                    value={formData.fiscalYearId}
                    label="Fiscal Year"
                    onChange={handleChange}
                    sx={{ borderRadius: 2 }}
                  >
                    {fetchingFiscal ? (
                      <MenuItem disabled value="">
                        Loading...
                      </MenuItem>
                    ) : availableYears.length === 0 ? (
                      <MenuItem disabled value="">
                        No years found
                      </MenuItem>
                    ) : (
                      availableYears.map((yearKey) => (
                        <MenuItem key={yearKey} value={yearKey}>
                          {yearKey}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>

              {/* Quarter */}
              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  required
                  disabled={!formData.fiscalYearId || fetchingFiscal}
                >
                  <InputLabel>Quarter</InputLabel>
                  <Select
                    name="quarterId"
                    value={formData.quarterId}
                    label="Quarter"
                    onChange={handleChange}
                    sx={{ borderRadius: 2 }}
                  >
                    {!formData.fiscalYearId ? (
                      <MenuItem disabled value="">
                        Select Fiscal Year first
                      </MenuItem>
                    ) : availableQuarters.length === 0 ? (
                      <MenuItem disabled value="">
                        No quarters found
                      </MenuItem>
                    ) : (
                      availableQuarters.map((quarter) => (
                        <MenuItem key={quarter.id} value={quarter.id}>
                          {quarter.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>

              {/* Start Date */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Start Date"
                  name="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={handleChange}
                  required
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: todayDateString,
                  }}
                  InputProps={{
                    sx: { borderRadius: 2 },
                  }}
                />
              </Grid>

              {/* Grace Period Days */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Grace Period (Days)"
                  name="gracePeriodDays"
                  type="number"
                  value={formData.gracePeriodDays}
                  onChange={handleChange}
                  placeholder="e.g., 5"
                  required
                  inputProps={{ min: 1 }}
                  InputProps={{
                    sx: { borderRadius: 2 },
                  }}
                />
              </Grid>
            </Grid>

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "flex-end",
                mt: 4,
              }}
            >
              <Button
                variant="outlined"
                onClick={onClose}
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  px: 3,
                  borderColor: alpha(theme.palette.text.primary, 0.2),
                  color: "text.primary",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.text.primary, 0.05),
                    borderColor: alpha(theme.palette.text.primary, 0.3),
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading || fetchingFiscal || isFormEmpty}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  px: 4,
                  boxShadow: "none",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Save Exception"
                )}
              </Button>
            </Box>
          </form>
        </DialogContent>
      </Dialog>

      {/* Snackbar notification */}
      <NotificationSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </>
  );
}

export default AddNewRBMException;
